import React, { useEffect, useState } from 'react';
import {
  Boxes,
  CirclePlay,
  Handshake,
  KanbanSquare,
  LifeBuoy,
  Package,
  ShieldCheck,
  UsersRound,
  Wrench
} from 'lucide-react';
import api from '../services/api';
import './LandingPage.css';

const heroPoints = [
  { icon: Boxes, text: 'Track raw materials, products, orders, and workflow in one place' },
  { icon: ShieldCheck, text: 'Set up each company with only the tabs and access it needs' },
  { icon: CirclePlay, text: 'Train teams faster with guided onboarding and walkthrough videos' }
];

const subscriptionCards = [
  {
    icon: Handshake,
    title: 'Hands-on rollout',
    description: 'We help set up the workspace, tabs, access, and operating flow around how your company actually runs.'
  },
  {
    icon: CirclePlay,
    title: 'Training and walkthroughs',
    description: 'Book a guided demo first, then use shared videos and walkthroughs to train owners, admins, and operators.'
  },
  {
    icon: LifeBuoy,
    title: 'Ongoing subscription support',
    description: 'The subscription is structured around setup, usage guidance, and operational support instead of one-time software delivery.'
  }
];

const featureCards = [
  {
    icon: Package,
    title: 'Stock that stays believable',
    description: 'Follow inward, outward, hold, and finished-goods movements with fewer spreadsheet gaps and fewer surprises.'
  },
  {
    icon: Wrench,
    title: 'Raw materials with real structure',
    description: 'Define materials, characteristics, usage, and BOM dependencies so production decisions start from cleaner master data.'
  },
  {
    icon: KanbanSquare,
    title: 'Orders and workflow together',
    description: 'Create orders, move them through stages, and keep fulfilment visible without switching systems.'
  },
  {
    icon: UsersRound,
    title: 'Company-specific access',
    description: 'Enable only the features a company needs, then control what each user type can actually see and do.'
  }
];

const walkthroughCards = [
  {
    icon: ShieldCheck,
    title: 'Owner and admin setup',
    description: 'See how companies are launched, tabs are enabled, roles are assigned, and onboarding starts.',
    tag: 'Demo topic'
  },
  {
    icon: Package,
    title: 'Daily stock and movement flow',
    description: 'Follow how teams record movements, verify the ledger, and keep stock usable on a day-to-day basis.',
    tag: 'Walkthrough'
  },
  {
    icon: Boxes,
    title: 'Orders, BOM, and workflow',
    description: 'Understand how products, raw materials, orders, and workflow stages connect operationally.',
    tag: 'Walkthrough'
  }
];

const faqItems = [
  {
    question: 'Do you publish fixed pricing?',
    answer: 'No. We scope each setup based on the company, workflow depth, team size, and onboarding/support needs.'
  },
  {
    question: 'Can I book a demo before deciding anything?',
    answer: 'Yes. The main entry path is a booked demo, where we walk through the product and discuss what parts fit your operation.'
  },
  {
    question: 'Will you help us with setup and training?',
    answer: 'Yes. The subscription is meant to include rollout help, guided onboarding, and training support, not just software access.'
  },
  {
    question: 'Can different companies see different tabs and features?',
    answer: 'Yes. Each company can have a different workspace, and user roles inside that company can be restricted separately.'
  }
];

const initialFormState = {
  name: '',
  company_name: '',
  email: '',
  phone: '',
  industry: '',
  team_size: '',
  message: ''
};

function LandingPage() {
  const [openFaqKey, setOpenFaqKey] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const sessionKey = `landing_page_viewed:${window.location.pathname}`;
    if (sessionStorage.getItem(sessionKey) === 'true') return;
    sessionStorage.setItem(sessionKey, 'true');

    const referrerHost = (() => {
      try {
        if (!document.referrer) return 'direct';
        return new URL(document.referrer).hostname || 'direct';
      } catch (_err) {
        return 'direct';
      }
    })();
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || '';

    api.trackWebsiteEvent({
      event_type: 'page_view',
      source_path: window.location.pathname || '/',
      referrer_host: referrerHost,
      utm_source: utmSource
    }).catch(() => {});
  }, []);

  const trackDemoClick = (placement) => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || '';
    api.trackWebsiteEvent({
      event_type: 'demo_cta_click',
      source_path: window.location.pathname || '/',
      referrer_host: placement,
      utm_source: utmSource,
      metadata: { placement }
    }).catch(() => {});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError('');
    setFormSuccess('');
    try {
      await api.createContactLead({
        ...form,
        source_path: window.location.pathname || '/'
      });
      setForm(initialFormState);
      setFormSuccess('Thanks. Your demo request is in. We will reach out shortly.');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <header className="landing-topbar">
        <div className="landing-brand">
          <span className="landing-brand-mark">ICS</span>
          <div>
            <strong>Inventory Studio</strong>
            <small>Friendly inventory operations software</small>
          </div>
        </div>
        <div className="landing-topbar-actions">
          <a className="topbar-link" href="#book-demo" onClick={() => trackDemoClick('topbar')}>
            Book demo
          </a>
          <a className="topbar-button" href="/login">Sign in</a>
        </div>
      </header>

      <main className="landing-main">
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <p className="hero-kicker">Inventory software with rollout support</p>
            <h1>Friendly enough to introduce to your team. Serious enough to run daily operations.</h1>
            <p className="hero-subtitle">
              Inventory Studio helps companies track raw materials, products, orders, workflow, and access control without turning the first impression into a stiff corporate sales page.
            </p>
            <div className="hero-actions">
              <a className="hero-primary" href="#book-demo" onClick={() => trackDemoClick('hero_primary')}>
                Book a demo
              </a>
              <a className="hero-secondary" href="/login">Existing customer sign in</a>
            </div>
            <div className="hero-points">
              {heroPoints.map((point) => {
                const Icon = point.icon;
                return (
                  <div key={point.text} className="hero-point">
                    <span className="hero-point-icon"><Icon size={16} /></span>
                    <span>{point.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <aside className="hero-story-card">
            <span className="story-chip">What the subscription is built around</span>
            <h2>Less “buy software”, more “get your operation live properly”.</h2>
            <p>
              Every deal is scoped around the company, the workflow, and the level of onboarding support needed. The first goal is clarity: what you need, how the workspace should look, and how your team will actually use it.
            </p>
            <div className="story-stack">
              <div>
                <strong>Book a demo</strong>
                <span>See the product and walk through your use case.</span>
              </div>
              <div>
                <strong>Shape the workspace</strong>
                <span>Enable only the tabs, forms, and access your company actually needs.</span>
              </div>
              <div>
                <strong>Roll out with guidance</strong>
                <span>Use onboarding and walkthroughs to get owners, admins, and operators comfortable faster.</span>
              </div>
            </div>
          </aside>
        </section>

        <section className="landing-section landing-subscription">
          <div className="section-heading">
            <h2>What the subscription includes</h2>
            <p>No public pricing table here. The point is a better fit, not a generic plan grid.</p>
          </div>
          <div className="subscription-grid">
            {subscriptionCards.map((card) => {
              const Icon = card.icon;
              return (
                <article key={card.title} className="subscription-card">
                  <div className="landing-card-header">
                    <div className="landing-card-icon"><Icon size={18} /></div>
                    <h3>{card.title}</h3>
                  </div>
                  <p>{card.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="landing-section">
          <div className="section-heading">
            <h2>What companies actually use it for</h2>
            <p>Start with operational clarity, not with a giant feature dump.</p>
          </div>
          <div className="feature-grid">
            {featureCards.map((feature) => {
              const Icon = feature.icon;
              return (
                <article key={feature.title} className="feature-card">
                  <div className="landing-card-header">
                    <div className="landing-card-icon"><Icon size={18} /></div>
                    <h3>{feature.title}</h3>
                  </div>
                  <p>{feature.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="landing-section landing-walkthroughs">
          <div className="section-heading">
            <h2>Demo and walkthrough topics</h2>
            <p>You mentioned videos. This section gives you a clean place to attach those later without changing the page structure again.</p>
          </div>
          <div className="walkthrough-grid">
            {walkthroughCards.map((card) => {
              const Icon = card.icon;
              return (
                <article key={card.title} className="walkthrough-card">
                  <div className="walkthrough-card-top">
                    <span className="walkthrough-tag">{card.tag}</span>
                    <div className="landing-card-icon"><Icon size={18} /></div>
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                  <a href="#book-demo" onClick={() => trackDemoClick(`walkthrough:${card.title}`)}>
                    Book a demo to see this flow
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        <section className="landing-section landing-faq">
          <div className="section-heading">
            <h2>Quick questions</h2>
            <p>Short answers before you book the demo.</p>
          </div>
          <div className="faq-list">
            {faqItems.map((item) => (
              <div key={item.question} className="faq-item">
                <button
                  type="button"
                  className="faq-question"
                  onClick={() => setOpenFaqKey((prev) => (prev === item.question ? null : item.question))}
                  aria-expanded={openFaqKey === item.question}
                >
                  <span>{item.question}</span>
                  <span className="faq-toggle" aria-hidden="true">{openFaqKey === item.question ? '−' : '+'}</span>
                </button>
                {openFaqKey === item.question && <p>{item.answer}</p>}
              </div>
            ))}
          </div>
        </section>

        <section id="book-demo" className="landing-section landing-contact">
          <div className="contact-copy">
            <p className="hero-kicker">Book a demo</p>
            <h2>Tell us about your company and we will take it from there.</h2>
            <p>
              Use the form to request a demo. We save every request in the system and send it to the superadmin side as a real lead, so follow-up stays organized.
            </p>
            <div className="contact-note-list">
              <span>Friendly intro call</span>
              <span>Product walkthrough</span>
              <span>Workflow fit discussion</span>
            </div>
          </div>
          <form className="contact-form-card" onSubmit={handleSubmit}>
            <div className="contact-form-grid">
              <label>
                Name
                <input value={form.name} onChange={(e) => updateForm('name', e.target.value)} required />
              </label>
              <label>
                Company
                <input value={form.company_name} onChange={(e) => updateForm('company_name', e.target.value)} required />
              </label>
              <label>
                Email
                <input type="email" value={form.email} onChange={(e) => updateForm('email', e.target.value)} required />
              </label>
              <label>
                Phone
                <input value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} />
              </label>
              <label>
                Industry
                <input value={form.industry} onChange={(e) => updateForm('industry', e.target.value)} placeholder="Manufacturing, trading, job work..." />
              </label>
              <label>
                Team size
                <input value={form.team_size} onChange={(e) => updateForm('team_size', e.target.value)} placeholder="Example: 10-25 users" />
              </label>
              <label className="contact-form-wide">
                What should we understand before the demo?
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => updateForm('message', e.target.value)}
                  placeholder="Tell us what you want to track, who will use the system, or what is broken today."
                  required
                />
              </label>
            </div>
            {formError && <div className="contact-form-feedback is-error">{formError}</div>}
            {formSuccess && <div className="contact-form-feedback is-success">{formSuccess}</div>}
            <div className="contact-form-actions">
              <button type="submit" className="hero-primary" disabled={submitting}>
                {submitting ? 'Sending...' : 'Request demo'}
              </button>
              <span className="helper-text">No public pricing page. We will scope it with you directly.</span>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

export default LandingPage;
